import * as Sentry from '@sentry/react'
import { scrubText, scrubValue } from './sentryRedact'

// Crash/error reporting - opt-in via VITE_SENTRY_DSN (see .env.example),
// same pattern as VITE_VAPID_PUBLIC_KEY. Unset = no-op, so a plain local
// dev environment behaves exactly as before this existed.
//
// Scope (2026-09-16, roadmap #12 first slice): SDK init + automatic
// unhandled-error/promise-rejection capture only. Release tagging (this
// slice) reuses deploy.sh's existing `VITE_APP_VERSION="$VERSION" npm run
// build` (itself `git describe`-based, not literally DEPLOY_ID - that's
// the backend's own per-deploy identifier via Docker ARG, baked into Go
// binaries via -ldflags instead) - falls back to "dev" for a plain local
// `vite build`/`vite dev` where that env var is never set. Source-map
// upload on build is still NOT done - see docs/CLAUDE_ZMIST_ROADMAP.md's
// #12.
//
// PII/secret scrubbing (2026-09-18 slice): beforeSend/beforeBreadcrumb
// below scrub every event and breadcrumb through ./sentryRedact before
// they leave the browser - see that module's doc comment for why (the Cue
// signaling secret and AI provider API keys currently sitting in
// localStorage per roadmap finding R6). `sendDefaultPii` is left unset
// (false, the SDK default) - do not turn it on without an explicit
// product decision, since it also starts attaching cookies and the full
// request/response bodies of any HTTP instrumentation.
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return
  Sentry.init({
    dsn,
    integrations: [Sentry.browserTracingIntegration()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/app\.zmist\.co\//, /^https:\/\/zmist\.co\//, import.meta.env.VITE_AUTH_API_BASE],
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || 'dev',
    beforeSend(event) {
      event.message = scrubText(event.message)
      for (const ex of event.exception?.values ?? []) {
        ex.value = scrubText(ex.value)
      }
      if (event.extra) scrubValue(event.extra)
      if (event.contexts) scrubValue(event.contexts)
      if (event.tags) scrubValue(event.tags)
      if (event.request) {
        if (event.request.data !== undefined) event.request.data = scrubValue(event.request.data)
        if (event.request.headers) scrubValue(event.request.headers)
        // Cookies aren't useful in an error report - drop them outright
        // rather than trying to enumerate which ones might be sensitive.
        delete event.request.cookies
      }
      // event.user is never set today - nothing in this app calls
      // Sentry.setUser. Whether events should carry the reporting user's
      // email (support triage) vs. only an opaque id is a product
      // decision, not made here - see roadmap #12.
      for (const bc of event.breadcrumbs ?? []) {
        bc.message = scrubText(bc.message)
        if (bc.data) scrubValue(bc.data)
      }
      return event
    },
    beforeBreadcrumb(breadcrumb) {
      breadcrumb.message = scrubText(breadcrumb.message)
      if (breadcrumb.data) scrubValue(breadcrumb.data)
      return breadcrumb
    },
  })
}
